import { ArrowLeft, ArrowRight } from "lucide-react";
import CleButton from "@cle/components/CleButton";
import { usePrepareEfteruddannelseIndberetningForSigning } from "@/hooks/useEfteruddannelseIndberetning";
import type { PeriodeLektioner } from "@/interfaces/efteruddannelse";
import type { AwaitingEvidenceItem } from "@cle/models/awaitingEvidence";
import { redirectToSigningUrl } from "@cle/utils/redirectToSigningUrl";
import InfoModal from "@cle/components/InfoModal";
import ReportRulesInfoContent, {
  type ReportRuleCategory,
} from "@cle/components/ReportRulesInfoContent";
import ReportSubmitDialog from "./ReportSubmitDialog";

type ReportInstructionStep = {
  description: string;
  key: string;
  title: string;
};

type SelectedReportEvidenceItemIds = Record<string, string[]>;

type ReportInstructionPanelProps = {
  awaitingEvidence?: AwaitingEvidenceItem[];
  currentStep: ReportInstructionStep;
  currentStepIndex: number;
  missingLessons: number;
  onNextStep: () => void;
  onPreviousStep: () => void;
  openReportId: string;
  requiredLessons: number;
  reportedLessons: PeriodeLektioner;
  reportingPeriodId: string;
  selectedEvidenceItemIds: SelectedReportEvidenceItemIds;
  selectedLessons: number;
  totalSteps: number;
  transferredToNextLessons: PeriodeLektioner;
  /** Kategorierne bag lofterne og overførsel, så hjælpeteksten viser de tal, der faktisk regnes med. */
  ruleCategories: ReportRuleCategory[];
};

const ReportInstructionPanel = ({
  awaitingEvidence,
  currentStep,
  currentStepIndex,
  missingLessons,
  onNextStep,
  onPreviousStep,
  openReportId,
  requiredLessons,
  reportedLessons,
  reportingPeriodId,
  selectedEvidenceItemIds,
  selectedLessons,
  totalSteps,
  transferredToNextLessons,
  ruleCategories,
}: ReportInstructionPanelProps) => {
  const isOverviewStep = currentStep.key === "overview";
  const { mutateAsync: prepareForSigning, isPending } =
    usePrepareEfteruddannelseIndberetningForSigning();

  const handlePrepareForSigning = async (receiptEmails: string[]) => {
    const evidenceItemIds = Array.from(
      new Set(Object.values(selectedEvidenceItemIds).flat()),
    );

    const { signingUrl } = await prepareForSigning({
      id: openReportId,
      efteruddannelsesperiodeId: reportingPeriodId,
      lektionerIndberettet: reportedLessons,
      lektionerOverfoeresTilNaeste: transferredToNextLessons,
      kvitteringEmails: receiptEmails,
      evidenceItemIds,
    });

    redirectToSigningUrl(signingUrl);
  };

  return (
    <aside className="cle-report-detail__instruction-panel">
      {/* mb-3 på rækken erstatter det margin, tælleren selv havde, så afstanden
          ned til titlen er uændret. */}
      <div className="d-flex justify-content-between align-items-center gap-2 mb-3">
        <p className="cle-report-detail__step-count mb-0">
          {currentStepIndex + 1} / {totalSteps}
        </p>
        <InfoModal
          title="Sådan indberetter du"
          description="Hvilken dokumentation der kan vælges, kategoriernes maksimum, overførsel og signering"
          ariaLabel="Læs mere om, hvordan du indberetter"
        >
          <ReportRulesInfoContent
            categories={ruleCategories}
            includesAutomaticDispensations
          />
        </InfoModal>
      </div>
      <h2 className="cle-report-detail__instruction-title">
        {currentStep.title}
      </h2>
      <p>{currentStep.description}</p>
      {isOverviewStep ? (
        <p>
          Hvis tallene ser korrekte ud, kan du indsende rapporten. Ellers kan du
          gå tilbage og rette dine valg.
        </p>
      ) : (
        <p>
          Nederst i tabellen kan du se, hvor mange lektioner der er valgt for
          dette trin.
        </p>
      )}

      <div className="cle-report-detail__instruction-actions">
        <CleButton
          id="cle-report-previous-step"
          variant="outline"
          className="gap-2"
          disabled={currentStepIndex === 0}
          onClick={onPreviousStep}
        >
          <ArrowLeft size={16} aria-hidden strokeWidth={1.75} />
          {isOverviewStep ? "" : "Forrige"}
        </CleButton>
        {isOverviewStep ? (
          <ReportSubmitDialog
            awaitingEvidence={awaitingEvidence}
            isPreparing={isPending}
            missingLessons={missingLessons}
            onPrepareForSigning={handlePrepareForSigning}
            requiredLessons={requiredLessons}
            selectedLessons={selectedLessons}
          />
        ) : (
          <CleButton
            id="cle-report-next-step"
            className="gap-2"
            onClick={onNextStep}
          >
            Næste
            <ArrowRight size={16} aria-hidden strokeWidth={1.75} />
          </CleButton>
        )}
      </div>
    </aside>
  );
};

export default ReportInstructionPanel;
